import React from "react";
import { createPortal } from "react-dom";
import { Box, Tooltip } from "@mui/material";
import { Draggable } from "@hello-pangea/dnd";
import {
  PlaceOutlined,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";

interface SidebarLocationRowProps {
  locationId: string;
  locationName: string;
  machineCountLabel: string;
  draggableIndex: number;
  isChild?: boolean;
}

const SidebarLocationRow: React.FC<SidebarLocationRowProps> = ({
  locationId,
  locationName,
  machineCountLabel,
  draggableIndex,
  isChild,
}) => {
  return (
    <Draggable draggableId={`location::${locationId}`} index={draggableIndex}>
      {(provided, snapshot) => {
        const draggableElement = (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            sx={{
              padding: isChild
                ? "4px 8px 4px 24px" /* spec: child location padding */
                : "10px 8px", /* spec: parent location padding — match child row height */
              display: "flex",
              alignItems: "center",
              gap: "6px",
              boxSizing: "border-box",
              cursor: snapshot.isDragging ? "grabbing" : "grab",
              transition: snapshot.isDragging ? "none" : "background-color 0.15s ease",
              transform: snapshot.isDragging ? "rotate(2deg)" : "none",
              boxShadow: snapshot.isDragging
                ? "0 8px 24px rgba(0,0,0,0.15)"
                : "none",
              opacity: snapshot.isDragging ? 0.92 : 1,
              backgroundColor: snapshot.isDragging ? "#FFFFFF" : "transparent",
              borderRadius: "6px",
              zIndex: snapshot.isDragging ? 9999 : undefined,
              "&:hover": snapshot.isDragging
                ? {}
                : {
                    backgroundColor: "#F7F5F0", /* spec: input-bg hover */
                  },
            }}
          >
            {/* Location pin icon — spec: terracotta for parent, hidden for child */}
            {!isChild && (
              <PlaceOutlined
                sx={{
                  fontSize: 12, /* spec: 12px pin icon */
                  color: "#D4572B", /* spec: accent / terracotta */
                  flexShrink: 0,
                }}
              />
            )}

            {/* Location name — spec: parent Sora 12px/600, child Sora 12px/400 */}
            <Tooltip title={locationName} enterDelay={600}>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: isChild ? 400 : 600,
                  color: isChild ? "#4A4F57" /* spec: text-body */ : "#0C1117" /* spec: text-primary */,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {locationName}
              </Box>
            </Tooltip>

            {/* Machine count — spec: JetBrains Mono 10px/500 text-muted */}
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "10px",
                fontWeight: 500,
                color: "#8C9099", /* spec: text-muted */
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {machineCountLabel}
            </Box>
          </Box>
        );

        // Portal fix: render dragging element in document.body to escape
        // parent transforms, overflow, and position contexts
        if (snapshot.isDragging) {
          return createPortal(draggableElement, document.body);
        }

        return draggableElement;
      }}
    </Draggable>
  );
};

export default SidebarLocationRow;
